import { Button } from "antd";
import { useGetGlobalFormFieldsQuery } from "api/GlobalFormField/hooks";
import { useGetFormFieldsQuery } from "api/TenantSlug/FormField/hooks";
import { FormFieldParentFieldSelector } from "components/Drawers/FormFieldParentFieldDrawer/FormFieldParentFieldSelector";
import { createFormFieldSourceUpdate } from "components/Drawers/FormFieldParentFieldDrawer/formFieldSourceUpdate";
import { FormFieldSourceOption, getFormFieldSourceKey } from "components/Drawers/FormFieldParentFieldDrawer/types";
import {
    buildGlobalSourceValues,
    buildLocalSourceValues,
    buildOwnOverrideValues,
    FORM_FIELD_OVERRIDE_MAPPINGS,
    normalizeOverrideValue,
} from "components/Forms/FormFieldsForm/formFieldOverrides";
import { FormStructureFormData } from "components/Forms/FormFieldsForm/formSchema";
import { Drawer } from "components/UI/Drawer";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { useDrawer } from "hooks/useDrawer";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FormFieldOverride } from "types/enums";
import { DrawerId } from "types/models";

import FormFieldData = App.Data.FormFieldData;
import GlobalFormFieldData = App.Data.GlobalFormFieldData;

interface FormFieldParentFieldDrawerProps {
    formFieldIndex?: number;
    formFieldId?: string;
}

const mapGlobalField = (field: GlobalFormFieldData, usedSystemNames: Set<string>): FormFieldSourceOption => ({
    id: field.id,
    sourceType: "global",
    systemName: field.system_name,
    visibleDescription: field.visible_description,
    sourceValues: buildGlobalSourceValues(field),
    disabled: usedSystemNames.has(field.system_name),
});

const mapLocalField = (field: FormFieldData, usedSystemNames: Set<string>): FormFieldSourceOption => ({
    id: field.id || "",
    sourceType: "local",
    systemName: field.system_name,
    visibleDescription: field.visible_description,
    sourceValues: buildLocalSourceValues(field),
    disabled: usedSystemNames.has(field.system_name),
});

export const FormFieldParentFieldDrawer = ({ formFieldIndex, formFieldId }: FormFieldParentFieldDrawerProps) => {
    const { t } = usePaldockTranslation("drawers.form-field-parent-field");
    const { t: tC } = usePaldockTranslation("common");
    const id = DrawerId.ParentField;
    const { hide } = useDrawer();
    const { getValues, setValue } = useGeneralForm<FormStructureFormData>();
    const [selectedKey, setSelectedKey] = useState<string | null>();
    const formFields = getValues("formFields") || [];
    const drawerSearchParam = formFieldIndex !== undefined ? `${id}-fieldKey` : `${id}-formFieldId`;
    const resolvedFormFieldIndex = formFieldIndex ?? formFields.findIndex((field) => field.id === formFieldId);
    const formField = formFields[resolvedFormFieldIndex];
    const globalFieldsQuery = useGetGlobalFormFieldsQuery({
        filter: { sort: "weight" },
        page: { number: 1, size: 1000 },
    });
    const localFieldsQuery = useGetFormFieldsQuery({
        filter: {
            include:
                "formFieldSelectItems,formFieldRadioValues,validationScenarios,modificationScenarios,autocompleteScenarios",
            filter: { is_global: true },
            sort: "weight",
        },
        page: { number: 1, size: 1000 },
    });

    useEffect(() => {
        if (!formField) return;

        if (formField.global_form_field_id) {
            setSelectedKey(getFormFieldSourceKey("global", formField.global_form_field_id));
        } else if (formField.parent_form_field_id) {
            setSelectedKey(getFormFieldSourceKey("local", formField.parent_form_field_id));
        } else {
            setSelectedKey(null);
        }
    }, [formField?.global_form_field_id, formField?.parent_form_field_id]);

    const usedSystemNames = useMemo(
        () =>
            new Set(
                formFields.filter((_, index) => index !== resolvedFormFieldIndex).map((field) => field.system_name)
            ),
        [resolvedFormFieldIndex, formFields]
    );

    const globalFields = useMemo(
        () =>
            (globalFieldsQuery.data?.globalFormFields || [])
                .map((field) => mapGlobalField(field, usedSystemNames))
                .sort((a, b) => a.visibleDescription.localeCompare(b.visibleDescription)),
        [globalFieldsQuery.data?.globalFormFields, usedSystemNames]
    );
    const localFields = useMemo(
        () =>
            (localFieldsQuery.data?.formFields || [])
                .filter((field) => field.id && field.form_structure_id === null && field.global_form_field_id === null)
                .map((field) => mapLocalField(field, usedSystemNames))
                .sort((a, b) => a.visibleDescription.localeCompare(b.visibleDescription)),
        [localFieldsQuery.data?.formFields, usedSystemNames]
    );
    const allSources = [...globalFields, ...localFields];

    const handleSubmit = () => {
        if (!formField || resolvedFormFieldIndex < 0) {
            toast.error(t("messages.form_field_id_required"));
            return;
        }

        const selectedSource = selectedKey
            ? (allSources.find((source) => getFormFieldSourceKey(source.sourceType, source.id) === selectedKey) ?? null)
            : null;
        if (selectedKey && !selectedSource) {
            toast.error(t("messages.source_required"));
            return;
        }

        const sourceUpdate = createFormFieldSourceUpdate(formField, selectedSource);
        const setValueOptions = { shouldDirty: true, shouldValidate: true };
        const previousSourceKey = formField.global_form_field_id
            ? getFormFieldSourceKey("global", formField.global_form_field_id)
            : formField.parent_form_field_id
              ? getFormFieldSourceKey("local", formField.parent_form_field_id)
              : null;
        const sourceChanged = previousSourceKey !== selectedKey;

        let currentOwnValues = formField.override_own_values ?? buildOwnOverrideValues(formField);
        if (sourceChanged) {
            currentOwnValues = { ...currentOwnValues };
            (formField.overridden_attributes || []).forEach((override) => {
                FORM_FIELD_OVERRIDE_MAPPINGS[override].forEach(({ formProperty, contextProperty }) => {
                    currentOwnValues[contextProperty] = (formField as unknown as Record<string, unknown>)[formProperty];
                });
            });
            setValue(`formFields.${resolvedFormFieldIndex}.override_own_values`, currentOwnValues, setValueOptions);
        }

        setValue(`formFields.${resolvedFormFieldIndex}.system_name`, sourceUpdate.system_name, setValueOptions);
        setValue(
            `formFields.${resolvedFormFieldIndex}.global_form_field_id`,
            sourceUpdate.global_form_field_id,
            setValueOptions
        );
        setValue(
            `formFields.${resolvedFormFieldIndex}.parent_form_field_id`,
            sourceUpdate.parent_form_field_id,
            setValueOptions
        );
        if (sourceChanged) {
            setValue(`formFields.${resolvedFormFieldIndex}.overridden_attributes`, [], setValueOptions);
            setValue(
                `formFields.${resolvedFormFieldIndex}.override_source_values`,
                selectedSource?.sourceValues ?? null,
                setValueOptions
            );

            const valuesToDisplay = selectedSource?.sourceValues ?? currentOwnValues;
            Object.entries(FORM_FIELD_OVERRIDE_MAPPINGS).forEach(([override, mappings]) => {
                mappings.forEach(({ formProperty, contextProperty }) => {
                    if (!(contextProperty in valuesToDisplay)) return;

                    setValue(
                        `formFields.${resolvedFormFieldIndex}.${formProperty}` as never,
                        normalizeOverrideValue(
                            override as FormFieldOverride,
                            valuesToDisplay[contextProperty]
                        ) as never,
                        setValueOptions
                    );
                });
            });
            if (valuesToDisplay.system_name) {
                setValue(
                    `formFields.${resolvedFormFieldIndex}.system_name`,
                    String(valuesToDisplay.system_name),
                    setValueOptions
                );
            }
            if (valuesToDisplay.type) {
                setValue(`formFields.${resolvedFormFieldIndex}.type`, valuesToDisplay.type as never, setValueOptions);
            }
        }
        hide(id, drawerSearchParam);
    };

    const loading = globalFieldsQuery.isLoading || localFieldsQuery.isLoading;
    const hasError = globalFieldsQuery.isError || localFieldsQuery.isError;

    return (
        <Drawer
            drawerId={id}
            title={<DrawerTitle mainTitle={t("title")} />}
            searchParamsToClean={[drawerSearchParam]}
            extraButtons={
                <Button
                    type="primary"
                    size="large"
                    disabled={loading || hasError || resolvedFormFieldIndex < 0}
                    onClick={handleSubmit}
                >
                    {tC("save")}
                </Button>
            }
        >
            <FormFieldParentFieldSelector
                globalFields={globalFields}
                localFields={localFields}
                selectedKey={selectedKey ?? null}
                onSelect={setSelectedKey}
                loading={loading}
                error={hasError}
            />
        </Drawer>
    );
};
